import AddDiscountComponent from "./add-discount-component";

import { Divider, message } from "antd";
import { Modal, Select, Text, Badge } from "@mantine/core";
import React, { useState } from "react";
import LineItemsComponent from "./bill-items-component";
import { cedisLocale } from "../../helpers/utilities";
import { useAtom } from "jotai";
import {
  deleteItemAtom,
  getBillSubTotalAtom,
  resetBillAtom,
  billAtom,
  billItemsAtom,
  updateItemAtom,
  addItemAtom,
  servicesAtom,
  paymentsAtom,
  getTotalItemDiscountsAtom,
  getTotalBillDiscountAtom,
  getTotalDiscountAtom,
  redeemedBenefitsAtom,
  updatePaymentAtom,
  chargesAtom,
  getTotalChargesAtom,
  getTotalDueAtom,
  getBalanceAtom,
  draftBillIdAtom,
  getTotalPaymentsAtom,
} from "../../helpers/state/bill";
import { useQuery, useMutation, useQueryClient } from "react-query";
import _ from "lodash";
import {
  getAllCharges,
  getAllNetworkPatients,
  getAllServices,
  getBill,
  postBillDraft,
  putBill,
} from "../../helpers/api";

import AddNoteComponent from "./add-note-component";
import BillReceipt from "./receipt";
import { getUser } from "../../helpers/auth";
import { IconUser, IconReceipt, IconCreditCard } from "@tabler/icons";
import AddChargesComponent from "./add-charges";
import { format } from "date-fns";
import { RedeemBenefitsButton } from "./redeem-benefits-button";
import { RedeemBenefitsModal } from "./redeem-benefits-modal";
import { MembershipStatus } from "../shared";
import { CheckoutHandler } from "./checkout-handler";
import BillSummary from "./bill-summary";
import CheckOutComponent from "./checkout";
import { BillAttachments } from "./bill-attachments";
import { PAYMENT_METHODS, isBillDateEditableForUser, MEMBERSHIP_BENEFIT_TYPE } from "../../helpers/config";
import { DatePicker } from "@mantine/dates";

const NewBillForm = ({ onSuccess }) => {
  const [showRedeemBenefitsModal, setShowRedeemBenefitsModal] = useState(false);
  const [submitForReimbursement, setSubmitForReimbursement] = useState(false);
  const [bill, setBill] = useAtom(billAtom);
  const [charges, setCharges] = useAtom(chargesAtom);
  const [services, setServices] = useAtom(servicesAtom);
  const [selectedItems] = useAtom(billItemsAtom);
  const [, deleteItem] = useAtom(deleteItemAtom);
  const [, addItem] = useAtom(addItemAtom);
  const [, updateItem] = useAtom(updateItemAtom);
  const [, resetBill] = useAtom(resetBillAtom);
  const [subTotal] = useAtom(getBillSubTotalAtom);
  const [totalPayments] = useAtom(getTotalPaymentsAtom);
  const [payments, setPayments] = useAtom(paymentsAtom);
  const [totalItemDiscounts] = useAtom(getTotalItemDiscountsAtom);
  const [totalBillDiscount] = useAtom(getTotalBillDiscountAtom);
  const [totalDiscount] = useAtom(getTotalDiscountAtom);
  const [redeemedBenefits, setRedeemedBenefits] = useAtom(redeemedBenefitsAtom);
  const [totalCharges] = useAtom(getTotalChargesAtom);
  const [totalDue] = useAtom(getTotalDueAtom);
  const [balance] = useAtom(getBalanceAtom);
  const [draftBillId, setDraftBillId] = useAtom(draftBillIdAtom);
  const [pendingAttachmentFiles, setPendingAttachmentFiles] = useState([]);
  const [serverAttachments, setServerAttachments] = useState([]);
  const [attachmentsModalOpen, setAttachmentsModalOpen] = useState(false);

  const user = getUser();
  const canEditBillDate = isBillDateEditableForUser(user);

  const prepareBillData = () => {
    return {
      bill: {
        patientId: bill.patientId,
        discount: bill.discount || 0,
        discountType: bill.discountType || "value",
        note: bill.note || "",
        dueDate: bill.dueDate,
        ...(canEditBillDate && {
          billDate: bill.billDate ? new Date(bill.billDate) : new Date(),
        }),
        totalItemDiscounts,
        totalBillDiscount,
        totalDiscount,
        redeemedBenefits,
        membershipId: bill?.membershipId || null,
      },
      items: selectedItems.map((item) => ({
        serviceId: item.serviceId,
        rate: item.rate,
        quantity: item.quantity,
        discount: item.discount || 0,
        discountType: item.discountType || "value",
      })),
      payments: payments.map((payment) => ({
        paymentMethod: payment.paymentMethod,
        amount: payment.amount,
      })),
      charges: charges.map((charge) => ({
        chargeId: charge.chargeId,
        value: charge.value,
      })),
      redeemedBenefits,
      totalItemDiscounts,
      totalBillDiscount,
      totalDiscount,
      ...(submitForReimbursement && { submitForReimbursement: true }),
    };
  };

  const {
    handleCheckoutClick,
    isLoading: isCheckoutLoading,
    CheckoutConfirmationModal,
  } = CheckoutHandler({
    billData: prepareBillData(),
    totalDue,
    totalPayments,
    balance,
    isEditMode: false,
    isDraft: false,
    attachmentFiles: pendingAttachmentFiles,
    onSuccess: (data) => {
      queryClient.invalidateQueries(["bills"]);
      queryClient.invalidateQueries(["member-benefits"]);
      onSuccess();

      setModal({
        title: "Receipt",
        isOpen: true,
        content: <BillReceipt param="billId" query={data.id} />,
        size: 400,
        zIndex: 100,
      });

      resetBill();
      setSubmitForReimbursement(false);
      setPendingAttachmentFiles([]);
      setServerAttachments([]);
    },
    onError: (error) => { },
  });

  const queryClient = useQueryClient();

  React.useEffect(() => {
    if (redeemedBenefits.length === 0 && submitForReimbursement) {
      setSubmitForReimbursement(false);
    }
  }, [redeemedBenefits.length, submitForReimbursement]);

  React.useEffect(() => {
    const hasUnsavedChanges =
      selectedItems.length > 0 ||
      bill.note ||
      bill.discount > 0 ||
      charges.length > 0;

    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges && !draftBillId) {
        e.preventDefault();
        e.returnValue =
          "You have unsaved changes. Are you sure you want to leave?";
        return "You have unsaved changes. Are you sure you want to leave?";
      }
    };

    if (hasUnsavedChanges && !draftBillId) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [selectedItems, bill.note, bill.discount, charges, draftBillId]);

  const {
    data: patients,
    isFetching: fetchingPatients,
    refetch: fetchPatients,
  } = useQuery({
    queryFn: getAllNetworkPatients,
    queryKey: ["patients"],
  });

  const { isFetching: fetchingServices, refetch: fetchServices } = useQuery({
    queryFn: getAllServices,
    queryKey: ["services"],
    onSuccess: (data) => setServices(data),
  });

  const { data: serverCharges = [] } = useQuery({
    queryFn: () => getAllCharges(user.facility.id),
    queryKey: ["charges"],
    enabled: services?.length > 0,
  });

  const saveDraftMutation = useMutation({
    mutationFn: ({ draftData, files }) => postBillDraft(draftData, files),
    onSuccess: async (response, variables) => {
      if (response.status === 201) {
        const responseData = response.data.data;

        setDraftBillId(responseData.id);
        if (variables.files?.length) {
          try {
            const billData = await getBill(responseData.id);
            setServerAttachments(billData.attachments || []);
            setPendingAttachmentFiles([]);
          } catch (_) { }
        }
        // message.success(
        //   `Draft saved successfully! Bill #${responseData.billNumber}`
        // );
        queryClient.invalidateQueries(["bills"]);
        queryClient.invalidateQueries(["member-benefits"]);
        fetchPatients();
        fetchServices();

        setModal({
          title: "Status",
          isOpen: true,
          content: (
            <div className="text-center">
              <div className="mb-3">
                <IconReceipt size={48} color="green" className="mb-2" />
                <Text size="lg" weight={600} color="green">
                  Draft Saved Successfully!
                </Text>
              </div>
              <div className="text-left">
                <div className="d-flex justify-content-between mb-2">
                  <Text size="sm">Bill Number:</Text>
                  <Text size="sm" weight={600}>
                    {responseData.billNumber}
                  </Text>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <Text size="sm">Status:</Text>
                  <Badge color="yellow" size="sm">
                    Draft
                  </Badge>
                </div>
                <div className="d-flex justify-content-between">
                  <Text size="sm">Created:</Text>
                  <Text size="sm">
                    {format(
                      new Date(responseData.createdAt),
                      "MMM d, yyyy - h:mm a"
                    )}
                  </Text>
                </div>
              </div>
              <div className="mt-3">
                <Text size="xs" color="dimmed">
                  You can continue editing this bill or proceed to checkout when
                  ready.
                </Text>
              </div>
            </div>
          ),
          size: "md",
        });

        return;
      }

      throw response;
    },
    onError: (error) => {
      const err = error?.response?.data?.message;
      if (_.isArray(err)) {
        err?.map((err) => message.error(err.message));
      } else {
        message.error(err || "error saving draft!");
      }
    },
  });

  const updateDraftMutation = useMutation({
    mutationFn: ({ draftData, files, attachmentsToKeep }) => putBill(draftData, files, attachmentsToKeep),
    onSuccess: async (response, variables) => {
      if (response.status === 200) {
        const responseData = response.data.data;

        if (variables.files?.length || variables.attachmentsToKeep !== undefined) {
          try {
            const bid = variables.draftData?.bill?.id || draftBillId;
            if (bid) {
              const billData = await getBill(bid);
              setServerAttachments(billData.attachments || []);
              setPendingAttachmentFiles([]);
            }
          } catch (_) { }
        }
        message.success(
          `Draft updated successfully! Bill #${responseData?.billNumber ?? ""}`
        );

        queryClient.invalidateQueries(["bills"]);
        queryClient.invalidateQueries(["member-benefits"]);
        fetchPatients();
        fetchServices();

        setModal({
          title: "Draft Updated Successfully",
          isOpen: true,
          content: (
            <div className="text-center">
              <div className="mb-3">
                <IconReceipt size={48} color="blue" className="mb-2" />
                <Text size="lg" weight={600} color="blue">
                  Draft Updated Successfully!
                </Text>
              </div>
              <div className="text-left">
                <div className="d-flex justify-content-between mb-2">
                  <Text size="sm">Bill Number:</Text>
                  <Text size="sm" weight={600}>
                    {responseData.billNumber}
                  </Text>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <Text size="sm">Status:</Text>
                  <Badge color="yellow" size="sm">
                    Draft
                  </Badge>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <Text size="sm">Total Amount:</Text>
                  <Text size="sm" weight={600}>
                    {cedisLocale.format(responseData.totalAmount || 0)}
                  </Text>
                </div>
                <div className="d-flex justify-content-between">
                  <Text size="sm">Last Updated:</Text>
                  <Text size="sm">
                    {format(
                      new Date(
                        responseData.updatedAt || responseData.createdAt
                      ),
                      "dd/MM/yyyy HH:mm:ss"
                    )}
                  </Text>
                </div>
              </div>
              <div className="mt-3">
                <Text size="xs" color="dimmed">
                  Your draft has been updated with the latest changes.
                </Text>
              </div>
            </div>
          ),
          size: "md",
        });

        return;
      }

      throw response;
    },
    onError: (error) => {
      const err = error?.response?.data?.message;
      if (_.isArray(err)) {
        err.map((err) => message.error(err.message));
      } else {
        message.error(err || "error updating draft!");
      }
    },
  });

  const [modal, setModal] = useState({
    isOpen: false,
    title: "",
    content: null,
    size: "",
    zIndex: 60,
  });

  const selectedPatient = patients?.find((pa) => pa.id === bill.patientId);
  const hasMembership = selectedPatient?.membershipSubscription;

  const handleBenefitsRedemption = async (selectedBenefits) => {
    const totalRedeemedAmount = selectedBenefits.reduce((total, benefit) => {
      return total + parseFloat(benefit.amount || 0);
    }, 0);

    if (totalRedeemedAmount > totalDue) {
      message.error(
        `Care value of ${cedisLocale.format(
          totalRedeemedAmount
        )} exceeds total bill of ${cedisLocale.format(totalDue)}`
      );
      return;
    }

    const mergedBenefits = [...redeemedBenefits];

    selectedBenefits.forEach((newBenefit) => {
      const existingBenefitIndex = mergedBenefits.findIndex(
        (existing) => existing.careCategoryId === newBenefit.careCategoryId
      );

      if (existingBenefitIndex >= 0) {
        mergedBenefits[existingBenefitIndex] = {
          ...mergedBenefits[existingBenefitIndex],
          amount:
            mergedBenefits[existingBenefitIndex].amount + newBenefit.amount,
        };
      } else {
        mergedBenefits.push(newBenefit);
      }
    });

    setRedeemedBenefits(mergedBenefits);
    setBill({
      ...bill,
      redeemedBenefits: mergedBenefits,
    });

    if (totalRedeemedAmount > 0) {
      const existingRiviacarePayment = payments.find(
        (p) => p.paymentMethod === PAYMENT_METHODS.HEALTH_CREDIT
      );

      if (existingRiviacarePayment) {
        setPayments({
          recordId: existingRiviacarePayment.recordId,
          amount: existingRiviacarePayment.amount + totalRedeemedAmount,
        });
      } else {
        setPayments({
          paymentMethod: PAYMENT_METHODS.HEALTH_CREDIT,
          amount: totalRedeemedAmount,
        });
      }
    }

    setShowRedeemBenefitsModal(false);
  };

  const handleDeleteRedeemedBenefit = (benefitIndex) => {
    const benefitToDelete = redeemedBenefits[benefitIndex];
    const newRedeemedBenefits = redeemedBenefits.filter(
      (_, index) => index !== benefitIndex
    );

    setRedeemedBenefits(newRedeemedBenefits);
    setBill({
      ...bill,
      redeemedBenefits: newRedeemedBenefits,
    });

    if (benefitToDelete.amount > 0) {
      const existingRiviacarePayment = payments.find(
        (p) => p.paymentMethod === PAYMENT_METHODS.HEALTH_CREDIT
      );

      if (existingRiviacarePayment) {
        const newAmount =
          existingRiviacarePayment.amount - benefitToDelete.amount;

        setPayments({
          recordId: existingRiviacarePayment.recordId,
          amount: newAmount,
        });
      }
    }
  };

  const handleClearAllRedeemedBenefits = () => {
    const totalRedeemedAmount = redeemedBenefits.reduce(
      (sum, benefit) => sum + benefit.amount,
      0
    );

    setRedeemedBenefits([]);
    setBill({
      ...bill,
      redeemedBenefits: [],
    });

    if (totalRedeemedAmount > 0) {
      const existingRiviacarePayment = payments.find(
        (p) => p.paymentMethod === PAYMENT_METHODS.HEALTH_CREDIT
      );

      if (existingRiviacarePayment) {
        setPayments({
          recordId: existingRiviacarePayment.recordId,
          amount: 0,
        });
      }
    }
  };

  const handleAddCharges = () => {
    setModal({
      isOpen: true,
      title: "Add Bill Charge",
      content: (
        <div>
          <Text size="xs" color="dimmed">
            Add a charge to the total bill amount.
          </Text>
          <AddChargesComponent allCharges={serverCharges} />
        </div>
      ),
      size: "lg",
    });
  };

  const handleAddNote = () => {
    setModal({
      isOpen: true,
      title: "Add Bill Notes",
      content: (
        <div>
          <div className="mb-3">
            <Text size="xs" color="dimmed">
              Add notes or comments to this bill.
            </Text>
          </div>

          <div className="mb-3">
            <AddNoteComponent />
          </div>
        </div>
      ),
      size: "md",
    });
  };

  const handleAddDiscount = () => {
    setModal({
      isOpen: true,
      title: "Add Bill Discount",
      content: (
        <div>
          <Text size="xs" color="dimmed">
            Add a discount to the total bill amount.
          </Text>
          <AddDiscountComponent />
        </div>
      ),
      size: "md",
    });
  };

  const handleSaveDraft = () => {
    if (draftBillId) {
      return handleUpdateDraft();
    }

    if (!bill.patientId) {
      message.error("Please select a member.");
      return;
    }

    if (selectedItems.length === 0) {
      message.error("At least one line item is required.");
      return;
    }

    const invalidItems = selectedItems.filter(
      (item) => !item.serviceId || item.quantity <= 0
    );
    if (invalidItems.length > 0) {
      message.error(
        "Please complete all line items with valid service and quantity."
      );
      return;
    }

    if (redeemedBenefits.length > 0) {
      message.error(
        "Care value cannot be used during draft saving. Please remove care value usage before saving as draft."
      );
      return;
    }

    const riviacarePayments = payments.filter(
      (p) => p.paymentMethod === PAYMENT_METHODS.HEALTH_CREDIT
    );
    if (riviacarePayments.length > 0) {
      message.error(
        "Care value usage cannot be added during draft saving. Please remove care value usage before saving as draft."
      );
      return;
    }

    const draftData = {
      bill: {
        patientId: bill.patientId,
        note: bill.note || "",
        discount: bill.discount || 0,
        discountType: bill.discountType || "value",
        membershipId: bill.membershipId,
        redeemedBenefits: [],
        ...(canEditBillDate && {
          billDate: bill.billDate ? new Date(bill.billDate) : new Date(),
        }),
      },
      items: selectedItems,
      payments: payments.filter(
        (p) => p.paymentMethod !== PAYMENT_METHODS.HEALTH_CREDIT
      ),
      charges: charges,
    };

    saveDraftMutation.mutate({ draftData, files: pendingAttachmentFiles });
  };

  const handleUpdateDraft = () => {
    if (!bill.patientId) {
      message.error("Please select a member.");
      return;
    }

    if (selectedItems.length === 0) {
      message.error("At least one line item is required.");
      return;
    }

    if (redeemedBenefits.length > 0) {
      message.error(
        "Care value cannot be used during draft saving. Please remove care value usage before saving as draft."
      );
      return;
    }

    const riviacarePayments = payments.filter(
      (p) => p.paymentMethod === PAYMENT_METHODS.HEALTH_CREDIT
    );
    if (riviacarePayments.length > 0) {
      message.error(
        "Care value usage cannot be added during draft saving. Please remove care value usage before saving as draft."
      );
      return;
    }

    const draftData = {
      bill: {
        id: draftBillId,
        patientId: bill.patientId,
        note: bill.note || "",
        discount: bill.discount || 0,
        discountType: bill.discountType || "value",
        membershipId: bill.membershipId,
        redeemedBenefits: [],
        ...(canEditBillDate && {
          billDate: bill.billDate ? new Date(bill.billDate) : new Date(),
        }),
      },
      items: selectedItems,
      payments: payments.filter(
        (p) => p.paymentMethod !== PAYMENT_METHODS.HEALTH_CREDIT
      ),
      charges: charges,
    };

    updateDraftMutation.mutate({
      draftData,
      files: pendingAttachmentFiles,
      attachmentsToKeep: serverAttachments.map((a) => ({ fileUrl: a.fileUrl })),
    });
  };

  const handleAddPayments = () => {
    setModal({
      isOpen: true,
      title: "Add Payments",
      content: <CheckOutComponent />,
      size: "md",
    });
  };

  const stepCircle = {
    width: 28,
    height: 28,
    minWidth: 28,
    backgroundColor: "#0991cc",
    color: "#fff",
    fontSize: "0.85rem",
    fontWeight: 700,
  };

  const hasCareValueUsage =
    redeemedBenefits.length > 0 ||
    payments.some((p) => p.paymentMethod === PAYMENT_METHODS.HEALTH_CREDIT);

  return (
    <div className="row g-4">
      <Modal
        onClose={() => setModal({ ...modal, isOpen: false })}
        opened={modal.isOpen}
        title={modal.title}
        size={modal.size || "md"}
        zIndex={55}>
        {modal.content}
      </Modal>
      <RedeemBenefitsModal
        isOpen={showRedeemBenefitsModal}
        onClose={() => setShowRedeemBenefitsModal(false)}
        membershipId={hasMembership?.membershipId}
        totalBillAmount={totalDue}
        onRedeem={handleBenefitsRedemption}
      />
      {CheckoutConfirmationModal}

      <div className="col-12">
        <div className="d-flex flex-wrap gap-3 justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <div
              className="d-flex align-items-center justify-content-center rounded-circle me-3"
              style={{
                width: 44,
                height: 44,
                backgroundColor: "rgba(9, 145, 204, 0.12)",
              }}>
              <IconReceipt size={24} color="#0991cc" />
            </div>
            <div>
              <Text size="xl" weight={700} style={{ lineHeight: 1.1 }}>
                New Bill
              </Text>
              <Text size="sm" color="dimmed">
                Add a member and services, then check out or save for later.
              </Text>
            </div>
          </div>
          <Badge
            color={draftBillId ? "yellow" : "gray"}
            className={draftBillId ? "text-black" : "text-white"}
            size="lg"
            variant="filled">
            {draftBillId ? "Draft saved" : "Not saved"}
          </Badge>
        </div>
      </div>

      <div className="col-lg-8 col-12">
        <div className="border rounded-3 bg-white p-4 mb-4">
          <div className="d-flex align-items-center mb-3">
            <span
              className="d-inline-flex align-items-center justify-content-center rounded-circle me-3"
              style={stepCircle}>
              1
            </span>
            <div>
              <Text weight={600}>Member &amp; bill date</Text>
              <Text size="xs" color="dimmed">
                Select who this bill is for.
              </Text>
            </div>
          </div>
          <div className="row">
            <div className={canEditBillDate ? "col-md-8" : "col-12"}>
              <Text size="sm" weight={600} className="mb-2 d-block">
                Member
              </Text>
              <Select
                value={bill.patientId}
                onChange={(value) =>
                  setBill({
                    ...bill,
                    patientId: value,
                    membershipId:
                      patients?.find((pa) => pa.id === value)
                        ?.membershipSubscription?.membershipId || null,
                  })
                }
                placeholder="Select patient"
                data={
                  patients?.map((pa) => {
                    const hasCashlessCareValue =
                      pa.membershipSubscription?.id &&
                      pa.membershipSubscription?.benefitType !==
                      MEMBERSHIP_BENEFIT_TYPE.DISCOUNTED &&
                      pa.membershipSubscription?.benefitType !==
                      MEMBERSHIP_BENEFIT_TYPE.REGULAR;
                    return {
                      label: `${pa.firstName} ${pa.lastName} (${pa.contact})${hasCashlessCareValue ? " ✔️" : ""}`,
                      value: pa.id,
                    };
                  }) || []
                }
                searchable
                clearable
                size="md"
              />
            </div>

            {canEditBillDate && (
              <div className="col-md-4 mt-3 mt-md-0">
                <Text size="sm" weight={600} className="mb-2 d-block">
                  Bill date
                </Text>
                <DatePicker
                  value={bill.billDate ? new Date(bill.billDate) : new Date()}
                  onChange={(d) =>
                    setBill({ ...bill, billDate: d ? d.toISOString() : null })
                  }
                  size="md"
                  maxDate={new Date()}
                />
              </div>
            )}
          </div>

          <RedeemBenefitsButton
            hasMembership={hasMembership}
            patientName={
              patients?.find((pa) => pa.id === bill.patientId)?.firstName
            }
            subTotal={subTotal}
            onRedeemClick={() => setShowRedeemBenefitsModal(true)}
            disabled={false}
            showWarning={!!draftBillId}
            benefitType={selectedPatient?.membershipSubscription?.benefitType}
            subscriptionId={selectedPatient?.membershipSubscription?.id}
            onViewPlan={(subscriptionId) =>
              setModal({
                isOpen: true,
                title: "Membership Status",
                content: <MembershipStatus membershipId={subscriptionId} />,
                size: 1200,
              })
            }
          />
        </div>

        <div className="border rounded-3 bg-white p-4 mb-4">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center">
              <span
                className="d-inline-flex align-items-center justify-content-center rounded-circle me-3"
                style={stepCircle}>
                2
              </span>
              <div>
                <Text weight={600}>Services</Text>
                <Text size="xs" color="dimmed">
                  Add the services rendered on this bill.
                </Text>
              </div>
            </div>
            <button
              onClick={() => addItem()}
              disabled={fetchingServices}
              className={`button is-small is-success fw-bold ${fetchingServices && " is-loading"
                }`}>
              <span className="bi bi-plus-lg me-1" />
              Add item
            </button>
          </div>

          {selectedItems?.length > 0 ? (
            <>
              <div className="row fw-bold text-muted small mb-2 px-1">
                <div className="col-4">Service</div>
                <div className="col-2 text-center">Price</div>
                <div className="col-2">Qty</div>
                <div className="col-2">Discount</div>
                <div className="col-1 text-end">Total</div>
                <div className="col-1"></div>
              </div>
              <div className="border rounded">
                {selectedItems.map((item, i) => (
                  <LineItemsComponent
                    key={item.recordId}
                    item={item}
                    services={services}
                    onUpdate={updateItem}
                    onDelete={deleteItem}
                    canEditPrice={canEditBillDate}
                  />
                ))}
              </div>
              <div className="d-flex justify-content-between mt-3">
                <button
                  onClick={() => addItem()}
                  disabled={fetchingServices}
                  className={`button is-small is-success is-light fw-bold ${fetchingServices && " is-loading"
                    }`}>
                  <span className="bi bi-plus-lg me-1" />
                  Add item
                </button>
                <button
                  onClick={() => {
                    const hasUnsavedChanges =
                      selectedItems.length > 0 ||
                      bill.note ||
                      bill.discount > 0 ||
                      charges.length > 0;

                    if (hasUnsavedChanges && !draftBillId) {
                      if (
                        window.confirm(
                          "You have unsaved changes. Are you sure you want to clear all items?"
                        )
                      ) {
                        resetBill();
                        setDraftBillId(null);
                        setSubmitForReimbursement(false);
                      }
                    } else {
                      resetBill();
                      setDraftBillId(null);
                      setSubmitForReimbursement(false);
                    }
                  }}
                  className="button is-small is-danger is-light fw-bold">
                  <span className="bi bi-trash me-1" />
                  Clear all
                </button>
              </div>
            </>
          ) : (
            <div
              className="text-center py-5 text-muted rounded-3"
              style={{ border: "1px dashed #d0d5dd" }}>
              <IconReceipt size={40} className="mb-2" />
              <Text size="sm" className="mb-3">
                No services added yet
              </Text>
              <button
                onClick={() => addItem()}
                disabled={fetchingServices}
                className={`button is-small is-success fw-bold ${fetchingServices && " is-loading"
                  }`}>
                <span className="bi bi-plus-lg me-1" />
                Add your first item
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="col-lg-4 col-12">
        <div className="position-sticky" style={{ top: "1rem" }}>
          <div className="border rounded-3 bg-white p-4">
            <div className="d-flex align-items-center mb-2">
              <IconCreditCard size={20} className="me-2" />
              <Text weight={600}>Bill summary</Text>
            </div>

            <BillSummary
              subTotal={subTotal}
              totalCharges={totalCharges}
              totalDiscount={totalDiscount}
              totalDue={totalDue}
              totalPayments={totalPayments}
              balance={balance}
              totalItemDiscounts={totalItemDiscounts}
              totalBillDiscount={totalBillDiscount}
              billDiscount={bill.discount}
              billDiscountType={bill.discountType}
              redeemedBenefits={redeemedBenefits}
              onDeleteBenefit={handleDeleteRedeemedBenefit}
              onClearAllBenefits={handleClearAllRedeemedBenefits}
              hasNote={!!bill.note}
              hasDiscount={totalDiscount > 0}
              hasCharges={totalCharges > 0}
              isDraft={true}
              draftBillId={draftBillId}
            />

            <Divider className="my-3" />

            <Text
              size="xs"
              weight={700}
              color="dimmed"
              className="text-uppercase mb-2 d-block"
              style={{ letterSpacing: "0.04em" }}>
              Adjustments
            </Text>
            <div className="row g-2 mb-3">
              <div className="col-6">
                <button
                  title="Bill notes"
                  onClick={handleAddNote}
                  className={`button w-100 d-flex align-items-center justify-content-center ${!!bill.note ? "is-success is-light" : ""
                    }`}>
                  <span className="bi bi-file-earmark-text me-2" />
                  Notes
                </button>
              </div>
              <div className="col-6">
                <button
                  title="Bill discount"
                  onClick={handleAddDiscount}
                  className={`button w-100 d-flex align-items-center justify-content-center ${totalDiscount > 0 ? "is-success is-light" : ""
                    }`}>
                  <span className="bi bi-percent me-2" />
                  Discount
                </button>
              </div>
              <div className="col-6">
                <button
                  title="Bill charges"
                  onClick={handleAddCharges}
                  className={`button w-100 d-flex align-items-center justify-content-center ${totalCharges > 0 ? "is-success is-light" : ""
                    }`}>
                  <span className="bi bi-plus-circle me-2" />
                  Charges
                </button>
              </div>
              <div className="col-6">
                <button
                  title="Bill payments"
                  onClick={handleAddPayments}
                  className={`button w-100 d-flex align-items-center justify-content-center ${totalPayments > 0 ? "is-success is-light" : ""
                    }`}>
                  <span className="bi bi-cash-coin me-2" />
                  Payments
                </button>
              </div>
            </div>

            <button
              disabled={selectedItems.length < 1}
              onClick={handleCheckoutClick}
              className={`button h6 py-4 w-100 app-btn btn-prim text-white d-flex justify-content-center align-items-center mb-2 ${isCheckoutLoading && " is-loading"
                }`}>
              <span className="bi bi-check-circle me-2" />
              <span className="fw-bold">CHECK OUT</span>
            </button>

            <button
              onClick={handleSaveDraft}
              disabled={
                hasCareValueUsage ||
                saveDraftMutation.isLoading ||
                updateDraftMutation.isLoading
              }
              className={`button h6 py-4 w-100 app-btn d-flex justify-content-center align-items-center mb-2 ${hasCareValueUsage ? "bg-light text-muted" : "bg-warning text-black"
                } ${saveDraftMutation.isLoading || updateDraftMutation.isLoading
                  ? "is-loading"
                  : ""
                }`}
              title={
                redeemedBenefits.length > 0
                  ? "Remove care value usage to enable saving"
                  : payments.some(
                    (p) => p.paymentMethod === PAYMENT_METHODS.HEALTH_CREDIT
                  )
                    ? "Remove care value payment method to enable saving"
                    : "Save bill as draft"
              }>
              <span className="bi bi-save me-2" />
              {draftBillId ? "UPDATE" : "SAVE"}
              {hasCareValueUsage && (
                <span className="bi bi-exclamation-triangle ms-2 text-warning" />
              )}
            </button>

            {draftBillId && (
              <button
                onClick={() => {
                  setModal({
                    isOpen: true,
                    title: "Preview Receipt",
                    size: "md",
                    content: <BillReceipt query={draftBillId} param="billId" />,
                    zIndex: 100,
                  });
                }}
                className="button h6 py-3 w-100 app-btn text-black d-flex justify-content-center align-items-center mb-2">
                <IconReceipt size={16} />
                <span className="ms-2">Preview Receipt</span>
              </button>
            )}

            <button
              type="button"
              className="button is-primary is-light w-100"
              onClick={() => setAttachmentsModalOpen(true)}>
              <span className="bi bi-paperclip me-2" />
              {serverAttachments.length + pendingAttachmentFiles.length === 0
                ? "Add attachments"
                : `Edit attachments (${serverAttachments.length + pendingAttachmentFiles.length}/5)`}
            </button>
            <Modal
              opened={attachmentsModalOpen}
              onClose={() => setAttachmentsModalOpen(false)}
              title="Bill attachments"
              size="md"
              zIndex={55}>
              <BillAttachments
                attachments={serverAttachments}
                pendingFiles={pendingAttachmentFiles}
                onFilesChange={setPendingAttachmentFiles}
                onRemoveAttachment={(a) =>
                  setServerAttachments((prev) =>
                    prev.filter((x) => x.id !== a.id)
                  )
                }
              />
            </Modal>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewBillForm;
